import React, { useEffect, useState, useRef } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Select,
  Spin,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import moment from "moment";
import UserServices from "../../services/UserServices";
import DashServices from "../../services/DashServices";
import PropTypes from "prop-types";

const { Option } = Select;

const simpleValidatorElement = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const AddPatientMedicine = ({
  subscription,
  patient,
  visible,
  onComplete,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMedicine, setLoadingMedicine] = useState(false);
  const [active, setActive] = useState(false);
  const [values, setValues] = useState({
    patient_id: null,
    medicine: null,
    time: [],
  });
  const [medicinesData, setMedicinesData] = useState(null);
  const [hoursData, setHoursData] = useState(null);
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: simpleValidatorElement,
      messages: {
        required: "Campo requerido",
        email: "El email no es válido",
      },
    }),
  );

  useEffect(() => {
    if (subscription !== null && visible === true) {
      getHours();
      loadMedicines();
    }
    if (visible !== null) {
      setActive(visible);
    }
    if (patient !== null) {
      setValues((v) => ({ ...v, patient_id: patient.id }));
    }
  }, [subscription, visible, patient]);

  const loadMedicines = async () => {
    setLoadingMedicine(true);
    try {
      const res = await DashServices.getMedicines(subscription.id);
      if (res.status >= 200 && res.status < 300) {
        setMedicinesData(res.data.medicines);
      }
      setLoadingMedicine(false);
    } catch (error) {
      console.log(error);
      setLoadingMedicine(false);
    }
  };

  const getHours = () => {
    let hrs = [];
    for (let i = 0; i < 48; i++) {
      let n = i % 2 == 0 ? i / 2 + ":00" : (i + 1) / 2 - 1 + ":30";
      if (Number.parseInt(n) < 10) {
        n = "0" + n;
      }
      hrs.push(n);
    }
    setHoursData(hrs);
  };

  const disabled = () => !simpleValidator.current.allValid();

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("Todos los campos son requeridos.");
      return;
    }
    try {
      const data = {
        ...values,
        days: [1, 2, 3, 4, 5, 6],
        dosage: 1.25,
        date_from: moment().format("YYYY-MM-DD"),
        date_to: moment().add(30, "days").format("YYYY-MM-DD"),
      };
      const res = await UserServices.assignMedicine(data);

      if (res) {
        if (res.data.message === "ok") {
          message.success("La medicina se agregó exitosamente.");
        } else {
          message.success(res.data.message, 3);
        }
        resetForm();
        setLoading(false);
        if (onComplete) {
          onComplete();
        }
      } else {
        setError("Log in error");
      }
    } catch (e) {
      console.error(e);
      setError(e.message);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setValues({
      patient_id: patient.id,
      medicine: null,
      time: [],
    });
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    forceUpdate();
  };

  const handleSelectChange = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
  };
  const handleSelectBlur = (name, value) => {
    simpleValidator.current.showMessageFor(name);
  };

  const handleCancel = () => {
    resetForm();
    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };
  return (
    <Modal
      open={active}
      title="Agregar medicamento"
      onOk={null}
      onCancel={handleCancel}
      footer={false}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          className="mb-6"
          closable
          onClose={() => setError("")}
        />
      )}
      <Form
        initialValues={{ remember: true }}
        onFinish={onFinish}
        autoComplete="off"
        className="w-full"
      >
        <Form.Item>
          <Select
            showSearch
            placeholder="Selecciona un medicamento"
            optionFilterProp="children"
            className="border-custom_primary"
            name="medicine"
            value={values.medicine}
            onChange={(v) => handleSelectChange("medicine", v)}
            onBlur={(v) => handleSelectBlur("medicine")}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            loading={loadingMedicine}
          >
            {medicinesData?.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
          {simpleValidator.current.message(
            "medicine",
            values.medicine,
            "required",
          )}
        </Form.Item>
        <Form.Item>
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Selecciona una hora"
            optionFilterProp="children"
            className="border-custom_primary"
            name="time"
            value={values.time}
            onChange={(v) => handleSelectChange("time", v)}
            onBlur={() => handleSelectBlur("time")}
          >
            {hoursData?.map((hour) => (
              <Option key={hour} value={hour}>
                {hour}
              </Option>
            ))}
          </Select>
          {simpleValidator.current.message("time", values.time, "required")}
        </Form.Item>
        <Form.Item>
          <Row align="end" gutter={16}>
            <Col>
              <Button key="back" onClick={handleCancel}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button
                disabled={disabled()}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
              >
                {loading ? (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                  />
                ) : (
                  "Enviar"
                )}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

AddPatientMedicine.propTypes = {
  subscription: PropTypes.object,
  patient: PropTypes.object,
  visible: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

export default AddPatientMedicine;
